const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}, _isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('token');

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const hasBody = options.body !== undefined && options.body !== null;

    const headers = {
      ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const config = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Access token expirado: tenta renovar com o refresh token e refazer a
      // requisição uma única vez, antes de desistir e propagar o erro.
      if (
        response.status === 401 &&
        !_isRetry &&
        endpoint !== '/login' &&
        endpoint !== '/refresh' &&
        endpoint !== '/auth/esqueci-senha' &&
        endpoint !== '/auth/redefinir-senha'
      ) {
        const renovou = await this.renovarAccessToken();
        if (renovou) {
          return this.request(endpoint, options, true);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Troca o refreshTokenHandle guardado por um access token novo.
  // Retorna true se conseguiu renovar, false se não (refresh ausente,
  // expirado ou revogado) — quem chamou decide o que fazer no caso false.
  // Chamadas concorrentes (várias requisições tomando 401 ao mesmo tempo)
  // compartilham a mesma tentativa em andamento, em vez de abrir uma
  // /refresh nova cada uma.
  async renovarAccessToken() {
    if (!this._refreshEmAndamento) {
      this._refreshEmAndamento = this._executarRenovacaoAccessToken().finally(() => {
        this._refreshEmAndamento = null;
      });
    }
    return this._refreshEmAndamento;
  }

  async _executarRenovacaoAccessToken() {
    const refreshTokenHandle = localStorage.getItem('refreshTokenHandle');
    if (!refreshTokenHandle) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseURL}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshTokenHandle }),
      });

      if (!response.ok) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshTokenHandle');
        return false;
      }

      const { accessToken, refreshTokenHandle: novoHandle } = await response.json();
      localStorage.setItem('token', accessToken);
      // O servidor rotaciona: o handle usado agora está revogado e só o novo
      // serve na próxima renovação.
      if (novoHandle) {
        localStorage.setItem('refreshTokenHandle', novoHandle);
      }
      return true;
    } catch (error) {
      console.error('Falha ao renovar access token:', error);
      return false;
    }
  }

  logout = async () => {
    const refreshTokenHandle = localStorage.getItem('refreshTokenHandle');
    if (refreshTokenHandle) {
      try {
        await fetch(`${this.baseURL}/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshTokenHandle }),
        });
      } catch (error) {
        console.error('Falha ao revogar refresh token no logout:', error);
      }
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshTokenHandle');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('cargoId');
    window.location.href = '/';
  };

  // Auth
  async login(credentials) {
    return this.request('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return this.request('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async solicitarRecuperacaoSenha(email) {
    return this.request('/auth/esqueci-senha', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async redefinirSenha(token, novaSenha, confirmarSenha) {
    return this.request('/auth/redefinir-senha', {
      method: 'POST',
      body: JSON.stringify({ token, novaSenha, confirmarSenha }),
    });
  }
    
  // Usuários
  async getCurrentUser() {
    return this.request('/me');
  }

  async getUsuarioById(id) {
    return this.request(`/usuarios/${String(id)}`);
  }

  async getUsuarioInstituicao(usuarioId) {
    return this.request(`/usuarios/${String(usuarioId)}/instituicao`);
  }

  async getUsuarioCursos(usuarioId) {
    return this.request(`/usuarios/${String(usuarioId)}/cursos`);
  }

  async enviarCadastroParaAnalise() {
    return this.request('/me/enviar-para-analise', { method: 'POST' });
  }

  async updateUserStatus(id, status) {
    return this.request(`/usuarios/${String(id)}/status`, {
      method: 'PUT',
      body: JSON.stringify(status),
    });
  }

  // Estágios
  async getVagasEstagio(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/vagas-estagio?${queryString}`);
  }

  async createVagaEstagio(data) {
    return this.request('/vagas-estagio', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVagaEstagio(id, data) {
    return this.request(`/vagas-estagio/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVagaEstagio(id) {
    return this.request(`/vagas-estagio/${id}`, {
      method: 'DELETE',
    });
  }

  // Instituições
  async getInstituicoes() {
    return this.request('/instituicoes');
  }

  async updateInstituicao(id, data) {
    return this.request(`/instituicoes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteInstituicao(id) {
    return this.request(`/instituicoes/${id}`, {
      method: 'DELETE',
    });
  }

  async getUserInstitution() {
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseURL}/usuarios/me/instituicao`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erro ao buscar dados da instituição');
    }

    return await response.json();
  }

  // Campi
  async getCampi(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/campis?${queryString}`);
  }

  async getCampusById(id) {
    return this.request(`/campis/${id}`);
  }

  async createCampus(data) {
    return this.request('/campis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampus(id, data) {
    return this.request(`/campis/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCampus(id) {
    return this.request(`/campis/${id}`, {
      method: 'DELETE',
    });
  }

  async getMyCampi() {
    return this.request('/usuarios/me/campis');
  }

  async getMyCursos() {
    return this.request('/usuarios/me/cursos');
  }

  // Buscar coordenador de estágio por CPF
  async buscarCoordenadorEstagio(cpf) {
    return this.request(`/cursos/coordenadores-estagio/buscar?cpf=${cpf}`);
  }

  // Cursos
  async getCursos(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/cursos?${queryString}`);
  }

  async createCurso(data) {
    return this.request('/cursos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCurso(id, data) {
    return this.request(`/cursos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCurso(id) {
    return this.request(`/cursos/${id}`, {
      method: 'DELETE',
    });
  }

  async getAreaCursos() {
    return this.request('/area-cursos', {
      method: 'GET'
    });
  }

  async createAreaCurso(data) {
    return this.request('/area-cursos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAreaCurso(id, data) {
    return this.request(`/area-cursos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAreaCurso(id) {
    return this.request(`/area-cursos/${id}`, {
      method: 'DELETE',
    });
  }

  // Estados e Cidades
  async getEstados() {
    return this.request('/estados');
  }

  async getCidadesByEstado(estadoId) {
    return this.request(`/estados/${estadoId}/cidades`);
  }

  async uploadTermoCooperacao(file) {
    console.log(file);
    const formData = new FormData();
    formData.append('file', file);

    console.log('Uploading Termo de Cooperacao', formData);

    return this.request('/documentos/termo-cooperacao', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadPlanoEnsino(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('/documentos/plano-ensino', {
      method: 'POST',
      body: formData,
    });
  }

  async downloadPlanoEnsino(solicitacaoId) {
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/documentos/plano-ensino/${solicitacaoId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `plano-de-ensino-${solicitacaoId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async getMeuTermo() {
    return this.request('/documentos/termo-cooperacao/me');
  }

  async getUsuarioTermo(usuarioId) {
    return this.request(`/documentos/termo-cooperacao/${String(usuarioId)}/info`);
  }

  async criarInstituicaoCadastro(payload) {
    return this.request('/instituicao-cadastros', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Usuários - Aprovação/Recusa
  async aprovarUsuario(usuarioId) {
    return this.request(`/usuarios/${usuarioId}/aprovar`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  }

  async recusarUsuario(usuarioId) {
    return this.request(`/usuarios/${usuarioId}/recusar`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  }

  async updateMe(payload) {
    return this.request('/me', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updatePassword(payload) {
    const response = await this.request('/me/password', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (response?.api_token) {
      localStorage.setItem('token', response.api_token);
    }
    if (response?.refreshTokenHandle) {
      localStorage.setItem('refreshTokenHandle', response.refreshTokenHandle);
    }

    return response;
  }

  async updateEmail(payload) {
    return this.request('/me/email', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Download de documentos
  async gerarTermoCompromissoAluno(alunoId, fallbackFilename = 'Termo de Compromisso.docx') {
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/termos-compromisso/alunos/${alunoId}/gerar`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : fallbackFilename;

    link.download = filename || fallbackFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  }

  async downloadTermoCooperacao(usuarioId) {
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/documentos/termo-cooperacao/${usuarioId}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `termo-cooperacao-${usuarioId}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, message: 'Download iniciado com sucesso' };
    } catch (error) {
      console.error('Erro ao baixar termo:', error);
      throw error;
    }
  }

  // Estabelecimentos de Saúde
  async createEstabelecimentoSaude(data) {
    return this.request('/estabelecimentos-saude', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEstabelecimentoSaude(id, data) {
    return this.request(`/estabelecimentos-saude/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getEstabelecimentosSaude() {
    return this.request('/estabelecimentos-saude', {
      method: 'GET',
    });
  }

  async getEstabelecimentosSaudeId(id) {
    return this.request(`/estabelecimentos-saude/${id}`, {
      method: 'GET',
    });
  }

  // Especialidades
  async getEspecialidades(estabelecimentoSaudeId = null) {
    const query = estabelecimentoSaudeId 
      ? `?estabelecimentoSaudeId=${estabelecimentoSaudeId}` 
      : '';
    return this.request(`/especialidades${query}`, {
      method: 'GET',
    });
  }

  async createEspecialidade(data) {
    return this.request('/especialidades', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Unidades Assistenciais
  async getUnidadesAssistenciais(especialidadeId = null) {
    const query = especialidadeId 
      ? `?especialidadeId=${especialidadeId}` 
      : '';
    return this.request(`/unidades-assistenciais${query}`, {
      method: 'GET',
    });
  }

  async createUnidadeAssistencial(data) {
    return this.request('/unidades-assistenciais', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Vagas
  async getVagas(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/vagas?${queryString}` : '/vagas';
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getVagaById(id) {
    return this.request(`/vagas/${id}`, {
      method: 'GET',
    });
  }

  async createVaga(data) {
    return this.request('/vagas', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVaga(id, data) {
    return this.request(`/vagas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVaga(id) {
    return this.request(`/vagas/${id}`, {
      method: 'DELETE',
    });
  }

  // Solicitações de Vaga
  async createSolicitacaoVaga(data) {
    return this.request('/solicitacoes-vaga', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSolicitacoesVaga(status = null, vagaId = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (vagaId) params.append('vagaId', String(vagaId));
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/solicitacoes-vaga${queryString}`, {
      method: 'GET',
    });
  }

  async concederSolicitacaoVaga(id, quantidadeAlunos = null) {
    const body = { status: 'CONCEDIDO' };
    if (quantidadeAlunos !== null) body.quantidadeAlunos = quantidadeAlunos;
    return this.request(`/solicitacoes-vaga/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async recusarSolicitacaoVaga(id, motivoNegacao = null) {
    const body = { status: 'RECUSADO' };
    if (motivoNegacao) body.motivoNegacao = motivoNegacao;
    return this.request(`/solicitacoes-vaga/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async confirmarSolicitacaoVaga(id) {
    return this.request(`/solicitacoes-vaga/${id}/confirmar`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  }

  async cancelarConcessaoSolicitacaoVaga(id) {
    return this.request(`/solicitacoes-vaga/${id}/cancelar-concessao`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  }

  async getDadosEstagio(solicitacaoId) {
    return this.request(`/solicitacoes-vaga/${solicitacaoId}/dados-estagio`, {
      method: 'GET',
    });
  }

  async saveDadosEstagio(solicitacaoId, data) {
    return this.request(`/solicitacoes-vaga/${solicitacaoId}/dados-estagio`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAlunosEstagio(solicitacaoId) {
    return this.request(`/solicitacoes-vaga/${solicitacaoId}/alunos-estagio`, {
      method: 'GET',
    });
  }

  async createAlunoEstagio(solicitacaoId, data) {
    return this.request(`/solicitacoes-vaga/${solicitacaoId}/alunos-estagio`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTermosCompromissoAlunos() {
    return this.request('/termos-compromisso/alunos', {
      method: 'GET',
    });
  }

  async uploadTermoCompromissoAluno(alunoId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/termos-compromisso/alunos/${alunoId}/upload`, {
      method: 'POST',
      body: formData,
    });
  }

  async downloadTermoCompromissoAluno(alunoId, fallbackFilename = 'termo-compromisso.pdf') {
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/termos-compromisso/alunos/${alunoId}/download`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : fallbackFilename;

    link.download = filename || fallbackFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  }

  async getRelatorioConcessao(relatorio, filtros = {}) {
    return this.request(`/relatorios/concessao/${relatorio}${this._consultaRelatorio(filtros)}`);
  }

  async getRelatorioConcessaoDetalhe(relatorio, solicitacaoId) {
    return this.request(`/relatorios/concessao/${relatorio}/${solicitacaoId}`);
  }

  async getOpcoesRelatorioConcessao(relatorio) {
    return this.request(`/relatorios/concessao/${relatorio}/opcoes`);
  }

  async downloadRelatorioConcessaoPdf(relatorio, filtros = {}) {
    return this._baixarPdf(
      `/relatorios/concessao/${relatorio}/pdf${this._consultaRelatorio(filtros)}`,
      `relatorio-${relatorio}.pdf`
    );
  }

  async downloadRelatorioConcessaoDetalhePdf(relatorio, solicitacaoId) {
    return this._baixarPdf(
      `/relatorios/concessao/${relatorio}/${solicitacaoId}/pdf`,
      `${relatorio}-${solicitacaoId}.pdf`
    );
  }

  _consultaRelatorio(filtros) {
    const parametros = new URLSearchParams();
    Object.entries(filtros).forEach(([chave, valor]) => {
      if (Array.isArray(valor)) {
        valor.forEach((item) => parametros.append(chave, item));
      } else if (valor !== undefined && valor !== null && valor !== '') {
        parametros.append(chave, valor);
      }
    });
    const consulta = parametros.toString();
    return consulta ? `?${consulta}` : '';
  }

  async _baixarPdf(endpoint, nomePadrao, _isRetry = false) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (response.status === 401 && !_isRetry && (await this.renovarAccessToken())) {
      return this._baixarPdf(endpoint, nomePadrao, true);
    }

    if (response.status === 429) {
      throw new Error('Você gerou muitos PDFs em pouco tempo. Aguarde um minuto e tente novamente.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const nomeDoServidor = response.headers
      .get('Content-Disposition')
      ?.match(/filename="?([^"]+)"?/)?.[1];

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = nomeDoServidor || nomePadrao;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Notificações
  async getContagemNotificacoes() {
    return this.request('/notificacoes/contagem');
  }

  async getNotificacoes() {
    return this.request('/notificacoes');
  }

  async marcarNotificacaoLida(id) {
    return this.request(`/notificacoes/${id}/lida`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async marcarTodasNotificacoesLidas() {
    return this.request('/notificacoes/marcar-todas-lidas', {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
